/**
 * Barre de progression vers la livraison offerte.
 *
 * Affiche le montant restant à ajouter au panier pour atteindre le franco de
 * port. C'est un des leviers les plus rentables d'un panier : la cliente sait
 * précisément ce qui lui manque au lieu de le découvrir au paiement.
 *
 * Seuils et phrases viennent des attributs posés par snippets/livraison-offerte
 * .liquid, qui seul connaît le marché et la langue. La France a deux paliers,
 * point relais puis domicile ; l'Europe et la Suisse n'en ont qu'un. Rien de
 * tout cela n'est écrit ici : ce fichier ne sait que calculer et dessiner.
 *
 * Le composant se met à jour sur l'événement standard Shopify
 * « shopify:cart:lines-update », émis à chaque modification du panier.
 */
class LivraisonOfferte extends HTMLElement {
  connectedCallback() {
    this.message = this.querySelector('.livraison-offerte__message');
    this.jauge = this.querySelector('.livraison-offerte__jauge');
    this.piste = this.querySelector('.livraison-offerte__piste');
    this.jalon = this.querySelector('.livraison-offerte__jalon');
    this.seuil = Number(this.dataset.seuil) || 8000;
    this.relais = Number(this.dataset.relais) || 0;
    this.langue = this.dataset.langue || document.documentElement.lang || 'fr';
    this.devise = this.dataset.devise || 'EUR';

    // Repli en français : si les attributs manquent (fragment d'une version
    // antérieure resté en cache), la barre parle encore plutôt que de rester
    // muette.
    this.dit = {
      atteint: this.dataset.ditAtteint || 'Livraison offerte',
      relais: this.dataset.ditRelais || 'Plus que {m} pour la livraison offerte',
      domicile: this.dataset.ditDomicile || 'Point relais offert. Plus que {m} à domicile',
      simple: this.dataset.ditSimple || 'Plus que {m} pour la livraison offerte',
    };

    this.rafraichir = this.rafraichir.bind(this);
    document.addEventListener('shopify:cart:lines-update', this.rafraichir);

    // Le rendu Liquid donne déjà le total au chargement : on évite une
    // requête inutile et le clignotement qui va avec.
    const initial = Number(this.dataset.total);
    if (Number.isFinite(initial)) this.dessiner(initial);
    else this.rafraichir();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.rafraichir);
  }

  async rafraichir() {
    try {
      const reponse = await fetch(window.Shopify?.routes?.root
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js');
      if (!reponse.ok) throw new Error('HTTP ' + reponse.status);
      const panier = await reponse.json();
      this.dessiner(panier.total_price);
    } catch (erreur) {
      // Un indicateur de confort ne doit jamais bloquer un achat.
      console.warn('[livraison-offerte] panier illisible :', erreur);
      this.hidden = true;
    }
  }

  formater(centimes) {
    return new Intl.NumberFormat(this.langue, {
      style: 'currency',
      currency: this.devise,
      minimumFractionDigits: Number.isInteger(centimes / 100) ? 0 : 2,
    }).format(centimes / 100);
  }

  /*
   * Compose le message en isolant le montant dans son propre élément : c'est
   * le seul mot que la cliente cherche, il porte une encre plus dense que le
   * reste de la phrase. On assemble des nœuds plutôt qu'une chaîne HTML.
   *
   * Le gabarit vient de Liquid et porte le jeton {m} à la place du montant.
   * Une phrase sans jeton, comme « Livraison offerte », s'écrit telle quelle.
   */
  ecrire(gabarit, montant) {
    this.message.textContent = '';
    const morceaux = gabarit.split('{m}');

    if (morceaux.length < 2 || montant === undefined) {
      this.message.textContent = morceaux.join('');
      return;
    }

    const valeur = document.createElement('span');
    valeur.className = 'livraison-offerte__montant';
    valeur.textContent = montant;
    this.message.append(morceaux[0], valeur, morceaux.slice(1).join('{m}'));
  }

  dessiner(total) {
    // Panier vide : rien à encourager, on n'occupe pas l'espace.
    if (!total) {
      this.hidden = true;
      return;
    }

    const restant = this.seuil - total;
    const atteint = restant <= 0;
    // Arrondi inférieur : la jauge ne doit être pleine qu'une fois le seuil
    // franchi, pas à 79,99 € sur 80 €.
    const pourcentage = atteint ? 100 : Math.min(99, Math.floor((total / this.seuil) * 100));

    const restantRelais = this.relais - total;
    if (atteint) {
      this.ecrire(this.dit.atteint);
    } else if (this.relais && restantRelais > 0) {
      this.ecrire(this.dit.relais, this.formater(restantRelais));
    } else if (this.relais) {
      this.ecrire(this.dit.domicile, this.formater(restant));
    } else {
      this.ecrire(this.dit.simple, this.formater(restant));
    }

    /*
     * Le jalon marque le premier palier sur le filet. Sans lui, la barre
     * paraît déjà pleine alors qu'il reste une étape, et la cliente ne
     * comprend pas ce qu'elle regarde. Hors de France il n'y a qu'un palier :
     * le jalon reste caché.
     */
    if (this.jalon) {
      const situer = this.relais > 0 && this.relais < this.seuil;
      this.jalon.hidden = !situer;
      if (situer) {
        this.jalon.style.insetInlineStart = (this.relais / this.seuil) * 100 + '%';
      }
    }

    this.jauge.style.inlineSize = pourcentage + '%';
    this.piste.setAttribute('aria-valuenow', String(pourcentage));
    // La phrase affichée dit déjà où l'on en est, dans la bonne langue : la
    // reprendre en étiquette évite un second texte à traduire, et à oublier.
    this.piste.setAttribute('aria-label', this.message.textContent);
    this.classList.toggle('livraison-offerte--atteint', atteint);
    this.hidden = false;
  }
}

if (!customElements.get('livraison-offerte')) {
  customElements.define('livraison-offerte', LivraisonOfferte);
}

/**
 * Le tiroir de panier est la principale interface d'achat du thème
 * (cart_type: drawer). Il est rendu par un snippet du thème qu'on ne veut pas
 * modifier : on y recopie donc la barre, et on la regreffe quand le tiroir
 * reconstruit son contenu après un ajout.
 */
function greffer() {
  const tiroir = document.querySelector(".cart-drawer__summary");
  if (!tiroir || tiroir.querySelector("livraison-offerte")) return;
  const source = document.querySelector("livraison-offerte");
  if (!source) return;
  const copie = source.cloneNode(true);
  /*
   * Le modèle porte le total du panier tel qu'il était au chargement de la
   * page, alors que le tiroir se reconstruit après chaque ajout. On efface
   * cette valeur : privée de total, la copie lit le panier réel au lieu de
   * répéter un montant périmé.
   */
  delete copie.dataset.total;
  tiroir.prepend(copie);
}

/*
 * Le tiroir réécrit son contenu à chaque ajout au panier, ce qui efface la
 * copie greffée. Un observateur la repose sans avoir à deviner quel événement
 * le thème émet à l'ouverture.
 */
function surveiller() {
  greffer();
  new MutationObserver(greffer).observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", surveiller);
} else {
  surveiller();
}
